import React from 'react'
import ReactDOM from 'react-dom'
import { PropTypes } from 'prop-types'
import { flowRight } from 'lodash'
import * as Nope from 'lib/utils/validation/Nope'
import * as Yup from 'yup'
import { Field, Form } from 'formik'
import { getVehicleTypeOptionsForVertical } from 'lib/utils/store/vehicle'
import { withErrorBoundaryNoRender } from 'lib/errorBoundary'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import { fetchPolicy } from 'lib/apolloFetchPolicy'

import VehicleProfileLogic from 'components/Vehicles/VehicleProfile/VehicleProfileLogic'
import ErrorComponent from 'components/Errors/ErrorComponent'
import ExtendedFormik from 'components/Forms/ExtendedFormik'
import FormElementGroup from 'components/FormElements/FormElementGroup'
import Button from 'components/UI/Button'
import Select from 'components/Forms/FormElements/Select'
import LoadingIndicator from 'components/LoadingIndicator'

import getVehicleClasses from 'graphql/vehicle/getVehicleClasses.gql'

const VehicleTypeChangeForm = ({
    renderButtonsPortalTarget,
    onSubmitted,
    vehicle,
    onCancel,
}) => {
    const { loading, error, data } = useQuery(getVehicleClasses, {
        fetchPolicy: fetchPolicy.CACHE_FIRST,
    })

    const { t } = useTranslation(['common', 'vehicleProfile', 'formElements'])

    if (error) return <ErrorComponent error={error} />
    if (loading) return <LoadingIndicator />

    const vehicleClasses = data.vehicleClasses

    const renderButtons = (submitForm, isSubmitting) => (
        <>
            <Button
                type="button"
                onClick={submitForm}
                className="button"
                loading={isSubmitting}
                disabled={isSubmitting}
                trackName="submit"
            >
                {t('common:save')}
            </Button>
            <Button
                type="button"
                className="button button--secondary"
                onClick={(e) => onCancel()}
                trackName="cancel"
            >
                {t('common:cancel')}
            </Button>
        </>
    )

    return (
        <>
            <p>{t('vehicleProfile:changeVehicleTypeNotice')}</p>
            <VehicleProfileLogic>
                {(vehicleProfileLogic) => (
                    <ExtendedFormik
                        id="vehicle-type-change-form"
                        initialValues={{
                            vehicleType: vehicle.vehicleType?.id ?? '',
                        }}
                        validationSchema={
                            Nope.schema({
                                vehicleType: Yup.string()
                                    .required('formElements:validation.required'),
                            })
                        }
                        onSubmit={async (values, actions) => {
                            const vehicleType = parseInt(values.vehicleType)
                            await vehicleProfileLogic.updateVehicle(vehicle.id, { vehicleType })
                                .then(() => {
                                    actions.setSubmitting(false)
                                    onSubmitted && onSubmitted()
                                })
                                .catch(() => {
                                    actions.setSubmitting(false)
                                })
                        }}
                    >
                        {({ submitForm, isSubmitting, handleChange }) => (
                            <Form className="vehicle-status-form">
                                <FormElementGroup>
                                    <Field name="vehicleType">
                                        {(props) => (
                                            <Select
                                                onChange={handleChange}
                                                {...props}
                                                label={t('vehicleProfile:vehicleType')}
                                                placeholder={t('vehicleProfile:sections.primary.choose.vehicleType')}
                                                options={Object.values(getVehicleTypeOptionsForVertical(t, vehicleClasses, vehicle))}
                                                required
                                            />
                                        )}
                                    </Field>
                                    {vehicleProfileLogic.error && (
                                        <ErrorComponent error={vehicleProfileLogic.error} container={false} />
                                    )}
                                    {renderButtonsPortalTarget && (
                                        ReactDOM.createPortal(
                                            renderButtons(submitForm, isSubmitting),
                                            renderButtonsPortalTarget
                                        )
                                    )}
                                    <div className="action-wrapper display-s-up">
                                        {renderButtons(submitForm, isSubmitting)}
                                    </div>
                                </FormElementGroup>
                            </Form>
                        )}
                    </ExtendedFormik>
                )}
            </VehicleProfileLogic>
        </>
    )
}

VehicleTypeChangeForm.propTypes = {
    onSubmitted: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    vehicle: PropTypes.object.isRequired,
    renderButtonsPortalTarget: PropTypes.object.isRequired,
}

export default flowRight(
    withErrorBoundaryNoRender,
)(VehicleTypeChangeForm)
